import {
  createContext,
  PropsWithChildren,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isValid, parseISO, startOfDay } from "date-fns";
import { useProduct } from "~/contexts/product-context";
import { useSalesInfo } from "~/pages/product-details/tabs/sales-data-tab/hooks/use-sales-info";
import {
  defaultSalesDateRange,
  earliestDefined,
  isValidSalesDateRange,
  lifetimeStartDate,
  parseIsoDate,
} from "~/shared/sales-data/sales-date-range";

export interface SalesDataRange {
  from?: Date;
  to?: Date;
  presetId?: string;
}

interface SalesDataContextValue {
  from?: Date;
  to?: Date;
  presetId?: string;
  setRange: (range: SalesDataRange) => void;
  firstMarketSaleDate?: Date;
  firstAmazonSaleDate?: Date;
  publishedAt?: Date;
  earliestDataDate?: Date;
  // Home market first (from GET /books/sales/info); empty until lookup resolves.
  markets: string[];
  lastSyncedAt?: Date;
  // Earliest of publication floor and first sale so pre-order windows stay selectable.
  earliestSelectableDate: Date;
  isFirstDatesLoading: boolean;
  // Lookup failed: missing earliestDataDate means unknown, not absent.
  firstDatesUnavailable: boolean;
  bundleIsbns: string[];
  setBundleIsbns: (isbns: string[]) => void;
  isBundled: boolean;
  // Resets on reload and product/bundle change (component state, not URL).
  marketFocus: string;
  setMarketFocus: (market: string) => void;
}

export const ALL_MARKETS = "all";

const SalesDataContext = createContext<SalesDataContextValue | undefined>(
  undefined,
);

const toContextRange = (earliest?: Date | null): SalesDataRange => {
  const { start, end } = defaultSalesDateRange(new Date(), earliest);
  if (!isValidSalesDateRange({ start, end })) {
    return { from: end, to: end };
  }
  return { from: start, to: end };
};

export function SalesDataProvider({ children }: PropsWithChildren) {
  "use no memo";

  // Storybook/tests mount without ProductProvider.
  const { product } = useProduct() ?? {};
  const releaseDate = useMemo(
    () => product?.publishedAt?.toDate(),
    [product?.publishedAt],
  );
  const productIsbn = product?.isbn;
  const isbns = useMemo(
    () => (productIsbn ? [productIsbn] : undefined),
    [productIsbn],
  );

  const [rangeState, setRangeState] = useState<SalesDataRange>(() =>
    toContextRange(releaseDate),
  );
  const [bundleIsbns, setBundleIsbns] = useState<string[]>([]);
  const [marketFocus, setMarketFocus] = useState<string>(ALL_MARKETS);
  const firstDateIsbns = useMemo(
    () => (bundleIsbns.length > 0 ? bundleIsbns : isbns),
    [bundleIsbns, isbns],
  );
  // Stop auto-seeding once the user picks a window.
  const userModifiedRef = useRef(false);

  const sourceKey = `${product?.id ?? "unknown"}:${isbns?.join(",") ?? ""}`;
  const previousSourceKeyRef = useRef(sourceKey);

  useEffect(() => {
    if (previousSourceKeyRef.current === sourceKey) {
      return;
    }
    previousSourceKeyRef.current = sourceKey;
    userModifiedRef.current = false;
    setBundleIsbns([]);
    setMarketFocus(ALL_MARKETS);
    setRangeState(toContextRange(releaseDate));
  }, [sourceKey, releaseDate]);

  const setRange = useCallback((next: SalesDataRange) => {
    userModifiedRef.current = true;
    setRangeState(next);
  }, []);

  const {
    data: salesInfo,
    isLoading: isFirstDatesLoading,
    isError: isFirstDatesError,
  } = useSalesInfo(firstDateIsbns);

  const firstMarketSaleDate = useMemo(
    () => parseIsoDate(salesInfo?.market),
    [salesInfo?.market],
  );
  const firstAmazonSaleDate = useMemo(
    () => parseIsoDate(salesInfo?.amazon),
    [salesInfo?.amazon],
  );
  const earliestDataDate = useMemo(
    () => earliestDefined(firstMarketSaleDate, firstAmazonSaleDate),
    [firstMarketSaleDate, firstAmazonSaleDate],
  );
  const lastSyncedAt = useMemo(() => {
    if (!salesInfo?.lastSyncedAt) {
      return undefined;
    }
    const parsed = parseISO(salesInfo.lastSyncedAt);
    return isValid(parsed) ? parsed : undefined;
  }, [salesInfo?.lastSyncedAt]);

  const markets = useMemo<string[]>(
    () => salesInfo?.markets ?? [],
    [salesInfo?.markets],
  );

  const publishedAt = useMemo(
    () => (releaseDate ? startOfDay(releaseDate) : undefined),
    [releaseDate],
  );
  const releaseLowerBound = useMemo(
    () => lifetimeStartDate(releaseDate),
    [releaseDate],
  );
  // Pre-order Amazon sales can predate publication.
  const earliestSelectableDate = useMemo(
    () =>
      earliestDefined(
        releaseLowerBound,
        firstMarketSaleDate,
        firstAmazonSaleDate,
      ) ?? releaseLowerBound,
    [releaseLowerBound, firstMarketSaleDate, firstAmazonSaleDate],
  );

  const earliestSelectableTime = earliestSelectableDate.getTime();
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the time value, not the Date identity
  useEffect(() => {
    if (userModifiedRef.current) {
      return;
    }
    setRangeState(toContextRange(earliestSelectableDate));
  }, [earliestSelectableTime]);

  const value = useMemo(
    () => ({
      from: rangeState.from,
      to: rangeState.to,
      presetId: rangeState.presetId,
      setRange,
      firstMarketSaleDate,
      firstAmazonSaleDate,
      publishedAt,
      earliestDataDate,
      markets,
      lastSyncedAt,
      earliestSelectableDate,
      isFirstDatesLoading:
        Boolean(firstDateIsbns?.length) && isFirstDatesLoading,
      firstDatesUnavailable:
        Boolean(firstDateIsbns?.length) && isFirstDatesError,
      bundleIsbns,
      setBundleIsbns,
      isBundled: bundleIsbns.length > 0,
      marketFocus,
      setMarketFocus,
    }),
    [
      rangeState,
      setRange,
      firstMarketSaleDate,
      firstAmazonSaleDate,
      publishedAt,
      earliestDataDate,
      markets,
      lastSyncedAt,
      earliestSelectableDate,
      isFirstDatesLoading,
      isFirstDatesError,
      firstDateIsbns,
      bundleIsbns,
      marketFocus,
    ],
  );

  return <SalesDataContext value={value}>{children}</SalesDataContext>;
}

export function useSalesData(): SalesDataContextValue {
  const context = use(SalesDataContext);
  if (!context) {
    throw new Error("useSalesData must be used within a SalesDataProvider");
  }
  return context;
}
